import { NO_ARGS, SINGLE_NUMERIC, threeNumeric, twoNumeric } from './_argShapes';
import { STUB_NOT_IMPLEMENTED } from './_stubs';
import type { EffectEntry } from './types';

export const slow: EffectEntry = {
  kind: 'slow',
  category: 'effect',
  family: 'time',
  doc: 'stretch pattern across N cycles',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('slow', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('slow', 'fromAst'),
};

export const fast: EffectEntry = {
  kind: 'fast',
  category: 'effect',
  family: 'time',
  doc: 'compress pattern into 1/N cycle',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('fast', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('fast', 'fromAst'),
};

export const rev: EffectEntry = {
  kind: 'rev',
  category: 'effect',
  family: 'time',
  doc: 'reverse the pattern within each cycle (no arg)',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('rev', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('rev', 'fromAst'),
};

export const palindrome: EffectEntry = {
  kind: 'palindrome',
  category: 'effect',
  family: 'time',
  doc: 'alternate forward/reverse each cycle (no arg)',
  args: NO_ARGS,
  toCode: () => STUB_NOT_IMPLEMENTED('palindrome', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('palindrome', 'fromAst'),
};

export const ply: EffectEntry = {
  kind: 'ply',
  category: 'effect',
  family: 'time',
  doc: 'repeat each event N times in place (2 doubles, 4 rolls)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('ply', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('ply', 'fromAst'),
};

export const stutter: EffectEntry = {
  kind: 'stutter',
  category: 'effect',
  family: 'time',
  doc: 'stutter each event N times with a time offset',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('stutter', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('stutter', 'fromAst'),
};

export const iter: EffectEntry = {
  kind: 'iter',
  category: 'effect',
  family: 'time',
  doc: 'rotate pattern by 1/N each cycle (forward)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('iter', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('iter', 'fromAst'),
};

export const iterBack: EffectEntry = {
  kind: 'iterBack',
  category: 'effect',
  family: 'time',
  doc: 'rotate pattern by 1/N each cycle (reverse)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('iterBack', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('iterBack', 'fromAst'),
};

export const early: EffectEntry = {
  kind: 'early',
  category: 'effect',
  family: 'time',
  doc: 'shift events earlier by N cycles',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('early', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('early', 'fromAst'),
};

export const late: EffectEntry = {
  kind: 'late',
  category: 'effect',
  family: 'time',
  doc: 'shift events later by N cycles',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('late', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('late', 'fromAst'),
};

export const linger: EffectEntry = {
  kind: 'linger',
  category: 'effect',
  family: 'time',
  doc: 'hold a fraction of each cycle (0.5 = half cycle held)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('linger', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('linger', 'fromAst'),
};

export const zoom: EffectEntry = {
  kind: 'zoom',
  category: 'effect',
  family: 'time',
  doc: 'show only a window: zoom(start, end) in cycles',
  args: twoNumeric(
    { name: 'start', doc: 'window start in cycles' },
    { name: 'end', doc: 'window end in cycles' },
  ),
  toCode: () => STUB_NOT_IMPLEMENTED('zoom', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('zoom', 'fromAst'),
};

export const compress: EffectEntry = {
  kind: 'compress',
  category: 'effect',
  family: 'time',
  doc: 'opposite of zoom — compress the pattern into [start, end]',
  args: twoNumeric(
    { name: 'start', doc: 'destination start in cycles' },
    { name: 'end', doc: 'destination end in cycles' },
  ),
  toCode: () => STUB_NOT_IMPLEMENTED('compress', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('compress', 'fromAst'),
};

export const swing: EffectEntry = {
  kind: 'swing',
  category: 'effect',
  family: 'time',
  doc: 'swing amount (0 straight, 0.3 mild, 0.6 heavy)',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('swing', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('swing', 'fromAst'),
};

export const swingBy: EffectEntry = {
  kind: 'swingBy',
  category: 'effect',
  family: 'time',
  doc: 'swing with an explicit ratio argument',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('swingBy', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('swingBy', 'fromAst'),
};

export const loopAt: EffectEntry = {
  kind: 'loopAt',
  category: 'effect',
  family: 'time',
  doc: 'loop the sample across N beats of the cycle',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('loopAt', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('loopAt', 'fromAst'),
};

export const pace: EffectEntry = {
  kind: 'pace',
  category: 'effect',
  family: 'time',
  doc: 'override how many cycles the pattern takes',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('pace', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('pace', 'fromAst'),
};

export const repeatCycles: EffectEntry = {
  kind: 'repeatCycles',
  category: 'effect',
  family: 'time',
  doc: 'repeat the first N cycles of the pattern',
  args: SINGLE_NUMERIC,
  toCode: () => STUB_NOT_IMPLEMENTED('repeatCycles', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('repeatCycles', 'fromAst'),
};

export const echo: EffectEntry = {
  kind: 'echo',
  category: 'effect',
  family: 'time',
  doc: 'native echo: echo(n, shift, feedback)',
  args: threeNumeric(
    { name: 'n', doc: 'number of repeats' },
    { name: 'shift', doc: 'time shift between repeats in cycles' },
    { name: 'feedback', doc: 'amplitude decay per repeat (0–1)' },
  ),
  toCode: () => STUB_NOT_IMPLEMENTED('echo', 'toCode'),
  fromAst: () => STUB_NOT_IMPLEMENTED('echo', 'fromAst'),
};
